import type { BackoffStrategy, RetryConfig } from '../types/config';
import { BridgeError, isRetryableError } from './BridgeError';

/**
 * Context passed to retry strategy
 */
export interface RetryContext {
  /** Current attempt number (1-based) */
  attempt: number;

  /** The error that triggered the retry */
  error: BridgeError;

  /** When the operation started */
  startTime: Date;

  /** When the last attempt occurred */
  lastAttemptTime: Date;

  /** Total elapsed time in ms */
  elapsedMs: number;
}

/**
 * Decision returned by retry strategy
 */
export interface RetryDecision {
  /** Whether to retry */
  shouldRetry: boolean;

  /** Delay before retry in ms */
  delayMs: number;

  /** Reason for the decision */
  reason?: string;
}

/**
 * Abstract retry strategy
 */
export abstract class RetryStrategy {
  /**
   * Decide whether to retry and how long to wait
   */
  abstract shouldRetry(context: RetryContext): RetryDecision;

  /**
   * Reset the strategy state
   */
  abstract reset(): void;
}

/**
 * Calculate delay using different backoff strategies
 */
function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  strategy: BackoffStrategy,
  jitter: number
): number {
  let delay: number;

  switch (strategy) {
    case 'linear':
      delay = baseDelayMs * attempt;
      break;

    case 'fibonacci': {
      // Fibonacci sequence: 1, 1, 2, 3, 5, 8, 13, 21, ...
      let a = 1, b = 1;
      for (let i = 2; i < attempt; i++) {
        const temp = a + b;
        a = b;
        b = temp;
      }
      delay = baseDelayMs * b;
      break;
    }

    case 'exponential':
    default:
      delay = baseDelayMs * Math.pow(2, attempt - 1);
      break;
  }

  // Apply jitter
  if (jitter > 0) {
    const jitterRange = delay * jitter;
    delay += (Math.random() * 2 - 1) * jitterRange;
  }

  // Cap at max delay
  return Math.min(Math.max(0, delay), maxDelayMs);
}

/**
 * Configurable backoff retry strategy
 */
export class BackoffRetryStrategy extends RetryStrategy {
  private readonly maxAttempts: number;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly backoff: BackoffStrategy;
  private readonly jitter: number;
  private readonly retryableErrors: Set<string>;

  constructor(config: RetryConfig) {
    super();
    this.maxAttempts = config.maxAttempts;
    this.baseDelayMs = config.baseDelayMs;
    this.maxDelayMs = config.maxDelayMs;
    this.backoff = config.backoff;
    this.jitter = config.jitter;
    this.retryableErrors = new Set(config.retryableErrors);
  }

  shouldRetry(context: RetryContext): RetryDecision {
    // Check max attempts
    if (context.attempt >= this.maxAttempts) {
      return {
        shouldRetry: false,
        delayMs: 0,
        reason: `Max attempts (${this.maxAttempts}) reached`,
      };
    }

    // Check if error is retryable
    if (!isRetryableError(context.error)) {
      return {
        shouldRetry: false,
        delayMs: 0,
        reason: `Error '${context.error.code}' is not retryable`,
      };
    }

    // Check if error code is in retryable list
    if (!this.retryableErrors.has(context.error.code)) {
      return {
        shouldRetry: false,
        delayMs: 0,
        reason: `Error code '${context.error.code}' not in retryable list`,
      };
    }

    // Calculate delay
    const delayMs = calculateBackoffDelay(
      context.attempt,
      this.baseDelayMs,
      this.maxDelayMs,
      this.backoff,
      this.jitter
    );

    return {
      shouldRetry: true,
      delayMs,
      reason: `Retrying after ${delayMs}ms (attempt ${context.attempt + 1}/${this.maxAttempts})`,
    };
  }

  reset(): void {
    // No state to reset for basic backoff strategy
  }
}

/**
 * Circuit breaker states
 */
export type CircuitState = 'closed' | 'open' | 'half-open';

/**
 * Circuit breaker configuration
 */
export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit */
  failureThreshold: number;

  /** Time to wait before trying half-open (ms) */
  resetTimeoutMs: number;

  /** Number of successful calls to close circuit from half-open */
  successThreshold: number;
}

/**
 * Circuit breaker retry strategy
 */
export class CircuitBreakerStrategy extends RetryStrategy {
  private failureCount = 0;
  private successCount = 0;
  private lastFailureTime: Date | null = null;
  private state: CircuitState = 'closed';

  constructor(
    private readonly config: CircuitBreakerConfig,
    private readonly delegate: RetryStrategy
  ) {
    super();
  }

  shouldRetry(context: RetryContext): RetryDecision {
    this.updateState();

    // If circuit is open, don't retry
    if (this.state === 'open') {
      return {
        shouldRetry: false,
        delayMs: 0,
        reason: 'Circuit breaker is open',
      };
    }

    // Delegate to underlying strategy
    const decision = this.delegate.shouldRetry(context);

    if (!decision.shouldRetry) {
      this.recordFailure();
    }

    return decision;
  }

  /**
   * Record a successful operation
   */
  recordSuccess(): void {
    if (this.state === 'half-open') {
      this.successCount++;
      if (this.successCount >= this.config.successThreshold) {
        this.state = 'closed';
        this.failureCount = 0;
        this.successCount = 0;
      }
    } else if (this.state === 'closed') {
      this.failureCount = 0;
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    this.updateState();
    return this.state;
  }

  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.delegate.reset();
  }

  private recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = new Date();

    if (this.state === 'closed' && this.failureCount >= this.config.failureThreshold) {
      this.state = 'open';
    } else if (this.state === 'half-open') {
      this.state = 'open';
      this.successCount = 0;
    }
  }

  private updateState(): void {
    if (this.state === 'open' && this.lastFailureTime) {
      const elapsed = Date.now() - this.lastFailureTime.getTime();
      if (elapsed >= this.config.resetTimeoutMs) {
        this.state = 'half-open';
        this.successCount = 0;
      }
    }
  }
}

/**
 * No retry strategy - never retries
 */
export class NoRetryStrategy extends RetryStrategy {
  shouldRetry(_context: RetryContext): RetryDecision {
    return {
      shouldRetry: false,
      delayMs: 0,
      reason: 'Retry disabled',
    };
  }

  reset(): void {
    // No state to reset
  }
}

/**
 * Create a retry strategy from configuration
 */
export function createRetryStrategy(config: RetryConfig): RetryStrategy {
  return new BackoffRetryStrategy(config);
}
